/**
 * Asset API hooks.
 * All server state for assets goes through these — never fetch directly in components.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import apiClient from "../api/client";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const assetKeys = {
  all: ["assets"],
  list: (params) => ["assets", "list", params],
  detail: (id) => ["assets", "detail", id],
  activity: (id) => ["assets", "activity", id],
  editLog: (id) => ["assets", "editLog", id],
  numberPreview: (entityId, categoryId) => [
    "assets",
    "numberPreview",
    entityId,
    categoryId,
  ],
  categories: ["asset-categories"],
  entities: ["entities"],
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useAssets(params = {}) {
  return useQuery({
    queryKey: assetKeys.list(params),
    queryFn: () => apiClient.get("/api/assets", { params }).then((r) => r.data),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

// Asset Summary (financial roles only): cost and book value at the end of
// `period` (YYYY-MM, default = current month), and always-current counts by
// condition and ownership. `entityId` omitted = all the user's entities.
export function useAssetSummary({ entityId, period, enabled = true } = {}) {
  const params = {};
  if (entityId) params.entityId = entityId;
  if (period) params.period = period;
  return useQuery({
    queryKey: ["assets", "summary", params],
    queryFn: () =>
      apiClient.get("/api/assets/summary", { params }).then((r) => r.data),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled,
  });
}

// Finance overview (financial roles only): entities side by side, installments
// ending over the next 12 months, and what cannot be valued. `period` (YYYY-MM,
// default = current month) is the month cost, book value and % depreciated are
// measured at; every count is current.
export function useFinanceOverview({ period, enabled = true } = {}) {
  return useQuery({
    queryKey: ["finance", "overview", period || ""],
    queryFn: () =>
      apiClient
        .get("/api/finance/overview", { params: period ? { period } : {} })
        .then((r) => r.data),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled,
  });
}

export function useAsset(id, options = {}) {
  const { via, ...queryOptions } = options;
  return useQuery({
    queryKey: [...assetKeys.detail(id), via || ""],
    queryFn: () =>
      apiClient
        .get(`/api/assets/${id}`, {
          params: via ? { via } : undefined,
        })
        .then((r) => r.data),
    enabled: !!id,
    staleTime: 30_000,
    ...queryOptions,
  });
}

export function useActivityLog(assetId) {
  return useQuery({
    queryKey: assetKeys.activity(assetId),
    queryFn: () =>
      apiClient.get(`/api/assets/${assetId}/activity`).then((r) => r.data),
    enabled: !!assetId,
  });
}

export function useEditLog(assetId, enabled = false) {
  return useQuery({
    queryKey: assetKeys.editLog(assetId),
    queryFn: () =>
      apiClient.get(`/api/assets/${assetId}/edits`).then((r) => r.data),
    enabled: !!assetId && enabled,
  });
}

// The year in the number is the ENTITY acquisition year; a blank date previews
// the "--" placeholder, not today's year.
// excludeAssetId: previewing an edit — rank it against the OTHER assets.
export function useAssetNumberPreview(entityId, categoryId, entityPurchaseDate, excludeAssetId) {
  return useQuery({
    queryKey: [
      ...assetKeys.numberPreview(entityId, categoryId),
      entityPurchaseDate,
      excludeAssetId,
    ],
    queryFn: () =>
      apiClient
        .get("/api/assets/number-preview", {
          params: { entityId, categoryId, entityPurchaseDate, excludeAssetId },
        })
        .then((r) => r.data.assetNumber),
    enabled: !!entityId && !!categoryId,
    staleTime: 5_000,
  });
}

export function useAssetCategories() {
  return useQuery({
    queryKey: assetKeys.categories,
    queryFn: () => apiClient.get("/api/categories").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

export function useEntities() {
  return useQuery({
    queryKey: assetKeys.entities,
    queryFn: () => apiClient.get("/api/entities").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

// Runtime flags. assetNumbersFrozen=false means asset numbers are still
// provisional (docs/adr/0001): the UI warns before label printing and lets GA
// correct an asset's entity directly.
export function useSystemConfig() {
  return useQuery({
    queryKey: ["system-config"],
    queryFn: () => apiClient.get("/api/config").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

export function useScannerScripts() {
  return useQuery({
    queryKey: ["scanner-scripts"],
    queryFn: () => apiClient.get("/api/scanner-scripts").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post("/api/assets", data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

export function useUpdateAsset(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.patch(`/api/assets/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: assetKeys.detail(id) });
      qc.invalidateQueries({ queryKey: assetKeys.all });
    },
  });
}

export function useArchiveAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }) =>
      apiClient
        .delete(`/api/assets/${id}`, { data: { notes } })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

export function useReassignAsset(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post(`/api/assets/${id}/reassign`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: assetKeys.detail(id) });
      qc.invalidateQueries({ queryKey: assetKeys.activity(id) });
    },
  });
}

export function useReturnAsset(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post(`/api/assets/${id}/return`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: assetKeys.detail(id) });
      qc.invalidateQueries({ queryKey: assetKeys.activity(id) });
    },
  });
}

export function useLogCheck(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post(`/api/assets/${id}/check`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: assetKeys.activity(id) }),
  });
}

// ---------------------------------------------------------------------------
// Storage contents
// ---------------------------------------------------------------------------
export function useStorageContents(assetId) {
  return useQuery({
    queryKey: ["storage-contents", assetId],
    queryFn: () =>
      apiClient.get(`/api/assets/${assetId}/contents`).then((r) => r.data),
    enabled: !!assetId,
  });
}

export function useCreateStorageContent(assetId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient
        .post(`/api/assets/${assetId}/contents`, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["storage-contents", assetId] });
      qc.invalidateQueries({ queryKey: ["content-suggestions", assetId] });
    },
  });
}

export function useUpdateStorageContent(assetId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, ...data }) =>
      apiClient
        .patch(`/api/assets/${assetId}/contents/${contentId}`, data)
        .then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["storage-contents", assetId] }),
  });
}

export function useDeleteStorageContent(assetId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contentId) =>
      apiClient
        .delete(`/api/assets/${assetId}/contents/${contentId}`)
        .then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["storage-contents", assetId] }),
  });
}

// ---------------------------------------------------------------------------
// File index
// ---------------------------------------------------------------------------
export function useFileIndex(assetId) {
  return useQuery({
    queryKey: ["file-index", assetId],
    queryFn: () =>
      apiClient.get(`/api/assets/${assetId}/file-index`).then((r) => r.data),
    enabled: !!assetId,
  });
}

export function useContentSuggestions(assetId) {
  return useQuery({
    queryKey: ["content-suggestions", assetId],
    queryFn: () =>
      apiClient
        .get(`/api/assets/${assetId}/content-suggestions`)
        .then((r) => r.data),
    enabled: !!assetId,
  });
}

export function useUploadFileIndex(assetId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient
        .post(`/api/assets/${assetId}/file-index`, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["file-index", assetId] });
      qc.invalidateQueries({ queryKey: assetKeys.detail(assetId) });
      qc.invalidateQueries({ queryKey: ["content-suggestions", assetId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
export function useDashboard() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiClient.get("/api/dashboard").then((r) => r.data),
    staleTime: 30_000,
  });
}
